import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { motion } from "motion/react"
import { Camera01, Check, Image01 } from "@untitledui/icons"
import { publicConsumerApi } from "@/lib/api/public-consumer"
import type { ImageEvaluation } from "@/lib/api/agents"
import { createLimiter } from "@/components/estimates/quick-estimate/concurrency"
import { FlowCta, FlowHeading, PhasePane, QuietLink, StickyActions } from "../../flow-chrome"
import { shotTitle } from "../capture-copy"
import { missingRequired } from "../shot-plan"
import { classifyPhoto } from "../camera/detector"
import type { VisionClient } from "../brain/vision-client"
import { CHECK_TIMEOUT_MS, readPhoto, withTimeout } from "../brain/check-photo"
import { judgeShot } from "../brain/combine"
import { topView, type ViewClass } from "../brain/head"
import type { ViewReading } from "../brain/vision-engine"
import type { PhoneCheck } from "../types"
import type { VehicleSide } from "@/lib/api/types"
import { preparePhoto } from "../uploads/prepare-photo"
import type { ShotUploads } from "../uploads/use-shot-uploads"
import type { CaptureState, PlannedShot, ShotId } from "../types"
import { sortIntoSlots } from "./sort-imports"

// "Already took photos?" Each picked photo is prepared (EXIF + GPS stripped),
// looked at on the phone by the car-view brain (which view of the car it
// shows), checked by the server's AI, sorted into the walk (sort-imports.ts),
// and anything that doesn't fit is kept as an extra. Then the camera only asks
// for what's still missing.

// The phone's check of a sorted photo, for the slot it went into.
function phoneCheckFor(slot: PlannedShot, reading: ViewReading | null): PhoneCheck | null {
  if (!reading) return null
  const v = judgeShot({ shot: slot, probs: reading.probs, turn: null, taillights: reading.taillights })
  const top = topView(reading.probs)
  return { verdict: v.kind, seen: v.kind === "wrong-corner" ? v.seen : null, view: top.view, confidence: top.p }
}

// The brain's view, as the side a photo is filed under (when it's sure).
const VIEW_SIDE: Partial<Record<ViewClass, VehicleSide>> = {
  FL: "FRONT_LEFT_CORNER",
  FR: "FRONT_RIGHT_CORNER",
  RL: "REAR_LEFT_CORNER",
  RR: "REAR_RIGHT_CORNER",
  front: "FRONT_CENTER",
  rear: "REAR_CENTER",
  left: "LEFT_SIDE",
  right: "RIGHT_SIDE",
}
const SURE = 0.6

export const MAX_IMPORT = 12

type ItemState = "working" | "placed" | "extra" | "queued" | "not-image"

interface Item {
  index: number
  name: string
  preview: string
  state: ItemState
  slot: PlannedShot | null
}

export function GalleryImport({
  files,
  plan,
  capture,
  vision,
  uploads,
  onTakeMissing,
  onReview,
}: {
  files: readonly File[]
  plan: readonly PlannedShot[]
  capture: CaptureState
  // The camera's vision, when it's running (sorts what the brain can't).
  vision: VisionClient | null
  uploads: Pick<ShotUploads, "adopt" | "submit">
  onTakeMissing: (ids: ShotId[]) => void
  onReview: () => void
}) {
  const picked = useMemo(() => files.slice(0, MAX_IMPORT), [files])
  const [items, setItems] = useState<Item[]>(() =>
    picked.map((f, index) => ({
      index,
      name: f.name,
      preview: URL.createObjectURL(f),
      state: "working",
      slot: null,
    })),
  )
  const [done, setDone] = useState(false)

  // Previews made here are this screen's own; adopted photos get the flow's.
  useEffect(() => () => items.forEach((i) => URL.revokeObjectURL(i.preview)), []) // eslint-disable-line react-hooks/exhaustive-deps

  const latest = useRef({ plan, capture, vision, uploads })
  useLayoutEffect(() => {
    latest.current = { plan, capture, vision, uploads }
  })

  // Leaving this screen cancels the import: nothing lands in the walk later,
  // over photos taken in the meantime.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    const limiter = createLimiter(3)
    const stamp = Date.now()
    const patch = (index: number, p: Partial<Item>) =>
      setItems((prev) => prev.map((it) => (it.index === index ? { ...it, ...p } : it)))

    void (async () => {
      const results = await Promise.all(
        picked.map((original, index) =>
          limiter.run(async () => {
            if (!alive.current) return null
            const prepared = await preparePhoto(original, original.name)
            if (!prepared.ok) {
              patch(index, { state: "not-image" })
              return null
            }
            const file = prepared.photo.file
            let framing: "wide" | "close" | null = null
            let viewSide: VehicleSide | null = null
            const reading = await withTimeout(readPhoto(file).catch(() => null), CHECK_TIMEOUT_MS)
            const top = reading ? topView(reading.probs) : null
            if (top && top.p >= SURE) {
              if (top.view === "closeup" || top.view === "sticker") framing = "close"
              else if (VIEW_SIDE[top.view]) {
                framing = "wide"
                viewSide = VIEW_SIDE[top.view]!
              }
            }
            const vision = latest.current.vision
            if (!framing && vision?.caps.detector && typeof createImageBitmap === "function") {
              let bitmap: ImageBitmap | null = null
              try {
                bitmap = await createImageBitmap(file)
                framing = await classifyPhoto(vision, bitmap)
              } catch {
                framing = null
              } finally {
                // (A full-size photo: tens of MB until it's closed.)
                bitmap?.close()
              }
            }
            let evaluation: ImageEvaluation | null = null
            try {
              evaluation = (await publicConsumerApi.evaluateImage(file)).evaluation
            } catch {
              evaluation = null
            }
            return { index, key: `gallery-${stamp}-${index}`, file, evaluation, framing, viewSide, reading }
          }),
        ),
      )

      if (!alive.current) return
      const { plan: livePlan, capture: liveCapture, uploads: up } = latest.current
      const ok = results.filter((r): r is NonNullable<typeof r> => r !== null)
      const checked = ok.filter((r) => r.evaluation)
      const { placed, extras } = sortIntoSlots(
        // What the phone saw wins; the server's side is the fallback.
        checked.map((r) => ({ key: r.key, side: r.viewSide ?? r.evaluation!.side ?? null, framing: r.framing })),
        livePlan,
        new Set(Object.keys(liveCapture.shots) as ShotId[]),
      )
      const byKey = new Map(ok.map((r) => [r.key, r]))
      for (const [shotId, key] of placed) {
        const r = byKey.get(key)
        const slot = livePlan.find((s) => s.id === shotId) ?? null
        if (!r || !slot) continue
        up.adopt(shotId, r.file, r.evaluation!, slot, "gallery", phoneCheckFor(slot, r.reading))
        patch(r.index, { state: "placed", slot })
      }
      for (const key of extras) {
        const r = byKey.get(key)
        if (!r) continue
        up.adopt(`extra:${key}`, r.file, r.evaluation!, null, "gallery")
        patch(r.index, { state: "extra" })
      }
      // Couldn't be checked (offline, server trouble): kept as extras, sent later.
      for (const r of ok.filter((x) => !x.evaluation)) {
        up.submit(`extra:${r.key}`, r.file, { source: "gallery", planned: null })
        patch(r.index, { state: "queued" })
      }
      setDone(true)
    })()
  }, [picked])

  const placed = items.filter((i) => i.state === "placed").length
  const missing = missingRequired(plan, capture)

  return (
    <PhasePane className="pt-2">
      <FlowHeading className="text-[28px] font-bold leading-[1.15] tracking-tight text-ink">
        {done
          ? placed > 0
            ? `We placed ${placed} of your ${picked.length} photo${picked.length === 1 ? "" : "s"}`
            : "We added your photos"
          : "Adding your photos…"}
      </FlowHeading>
      <p className="mt-2 text-[15px] leading-relaxed text-zinc-600" aria-live="polite">
        {done
          ? missing.length > 0
            ? `${missing.length} more to take with the camera. We'll show you exactly which.`
            : "That's everything the walk asks for."
          : `Checking which side of the car each one shows.`}
        {files.length > MAX_IMPORT && ` We used the first ${MAX_IMPORT}.`}
      </p>

      <ul className="mt-5 grid grid-cols-3 gap-2.5">
        {items.map((item) => (
          <li key={item.index} className="min-w-0">
            <div className="relative aspect-square overflow-hidden rounded-2xl bg-zinc-200">
              <img src={item.preview} alt="" className="size-full object-cover" />
              {item.state === "working" ? (
                <span className="absolute inset-0 grid place-items-center bg-black/30">
                  <span className="size-6 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden />
                </span>
              ) : item.state === "placed" ? (
                <motion.span
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="absolute right-1.5 top-1.5 grid size-7 place-items-center rounded-full bg-signal text-white"
                >
                  <Check className="size-4" strokeWidth={3} aria-hidden />
                </motion.span>
              ) : null}
            </div>
            <p className="mt-1 truncate text-[13px] font-medium text-zinc-600">
              {item.state === "placed" && item.slot
                ? shotTitle(item.slot)
                : item.state === "extra"
                  ? "Extra photo"
                  : item.state === "queued"
                    ? "Sends when online"
                    : item.state === "not-image"
                      ? "Not a photo"
                      : "Checking…"}
            </p>
          </li>
        ))}
      </ul>

      {done && (
        <StickyActions className="space-y-1">
          {missing.length > 0 ? (
            <>
              <FlowCta onClick={() => onTakeMissing(missing.map((s) => s.id))}>
                <Camera01 className="size-5" aria-hidden />
                {`Take the ${missing.length} missing photo${missing.length === 1 ? "" : "s"}`}
              </FlowCta>
              <QuietLink onClick={onReview}>
                <Image01 className="size-4" aria-hidden />
                Review photos
              </QuietLink>
            </>
          ) : (
            <FlowCta onClick={onReview}>Review photos</FlowCta>
          )}
        </StickyActions>
      )}
    </PhasePane>
  )
}
